import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function BulkConfirmReceived({ rows, selected, setSelected, bankId, methodId, onDone }) {
  const [busy, setBusy] = useState(false);
  const unresolved = rows.filter((row) => row.status !== "matched" && Number(row.bo_gross || 0) > Number(row.merchant_gross || 0));
  const chosen = unresolved.filter((row) => selected.has(row.variance_id));
  const [reference, setReference] = useState(() => `BULK-${new Date().toISOString().replace(/[-:TZ.]/g, "").slice(0, 14)}`);
  const allSelected = unresolved.length > 0 && chosen.length === unresolved.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(unresolved.map((row) => row.variance_id)));
  const confirm = async () => {
    if (!chosen.length) return toast.error("Select at least one unresolved row");
    if (!bankId || !reference.trim()) return toast.error("Bank account and bulk reference are required");
    const total = chosen.reduce((sum, row) => sum + Math.max(0, Number(row.bo_gross || 0) - Number(row.merchant_gross || 0)), 0);
    if (!window.confirm(`Confirm ${chosen.length} selected rows as received, totaling RM ${total.toLocaleString("en-MY", { minimumFractionDigits: 2 })}? This creates audited bank receipts and can be reversed from Upload History.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/sales/publicbank/manual-receipt/bulk", {
        bank_account_id: bankId, method_id: methodId, reference_prefix: reference.trim(),
        note: "Bulk confirmed from reconciliation", items: chosen.map((row) => ({
          branch_id: row.branch_id, txn_date: row.date,
          gross_amount: Math.max(0, Number(row.bo_gross || 0) - Number(row.merchant_gross || 0)),
        })),
      });
      toast.success(`${data.confirmed} rows confirmed · RM ${Number(data.gross_total || 0).toLocaleString("en-MY", { minimumFractionDigits: 2 })}`);
      setSelected(new Set()); setReference(`BULK-${Date.now()}`); onDone?.();
    } catch (error) { toast.error(apiError(error)); }
    finally { setBusy(false); }
  };
  return <div className="flex flex-wrap items-center gap-2 rounded-sm border border-blue-200 bg-blue-50 p-3">
    <Button size="sm" variant="outline" onClick={toggleAll}>{allSelected ? "Clear selection" : `Select all unresolved (${unresolved.length})`}</Button>
    <span className="text-sm font-medium text-blue-900">{chosen.length} selected</span>
    <Input className="h-9 w-56 bg-white" value={reference} onChange={(event) => setReference(event.target.value)} placeholder="Bulk bank reference" />
    <Button size="sm" onClick={confirm} disabled={busy || !chosen.length}>{busy ? "Confirming..." : "Confirm selected received"}</Button>
    <span className="text-xs text-blue-700">Only the remaining unreceived amount is added.</span>
  </div>;
}
